import type { GeolocationCoords } from '@/shared/hooks/useGeolocation';
import type { ThemeTokens, BrandColors } from '@/shared/ui/theme/tokens';
import { useToast } from '@/shared/ui/toast/ToastContext';

interface LocationStatusBadgeProps {
  coordinates: GeolocationCoords | null;
  loading: boolean;
  onRetry: () => void;
  T: ThemeTokens;
  C: BrandColors;
}

/**
 * Non-blocking device-health indicator — visible only while geolocation is
 * unavailable (denied/unsupported). Renders nothing once coordinates resolve,
 * so it never adds noise to the header during normal operation.
 *
 * Clickable: re-requests the browser's permission prompt when it's still in
 * 'prompt' state. Once explicitly denied, browsers block re-prompting from
 * JS entirely, so we detect that via the Permissions API and point the user
 * to their browser's site settings instead of silently failing again.
 */
export function LocationStatusBadge({ coordinates, loading, onRetry, T, C }: LocationStatusBadgeProps) {
  const { showToast } = useToast();

  if (loading || coordinates !== null) return null;

  const handleClick = async () => {
    if ('permissions' in navigator) {
      try {
        const status = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
        if (status.state === 'denied') {
          showToast(
            'La ubicación está bloqueada para este sitio. Habilitala desde la configuración del sitio en tu navegador.',
            { variant: 'warning', id: 'location-denied' },
          );
          return;
        }
      } catch {
        // Permissions API not fully supported (e.g. Safari) — fall through to retry
      }
    }
    onRetry();
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => { void handleClick(); }}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') void handleClick(); }}
      title="Ubicación desactivada — click para reintentar"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        color: C.danger,
        padding: '4px 10px',
        borderRadius: 999,
        background: T.surfaceBg,
        border: `1px solid ${C.danger}40`,
        cursor: 'pointer',
      }}
    >
      <span style={{ color: C.danger, display: 'inline-flex', alignItems: 'center' }}>
        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 21s7-5.686 7-11a7 7 0 1 0-14 0c0 5.314 7 11 7 11z" />
          <circle cx="12" cy="10" r="2.5" />
          <line x1="3" y1="3" x2="21" y2="21" />
        </svg>
      </span>
      <span style={{ fontSize: 12, fontWeight: 600, color: C.danger }}>
        Ubicación desactivada
      </span>
    </div>
  );
}
